import {
  planWboTypography,
  mergeWboTokens,
  wboPhotoBox,
  WBO_TYPOGRAPHY,
  type WboGraphicConfig,
  type WboLineHighlight,
  type WboTypographyPlan,
  type WboFormatChoice,
  type WboTokenOverrides,
} from './wbo-typography';

export type { WboGraphicConfig, WboLineHighlight } from './wbo-typography';
export type WboGraphicPlan = WboTypographyPlan;

const PANEL_BACKGROUND = '#111418';

function drawHighlightedLine(ctx: CanvasRenderingContext2D, line: WboTypographyPlan['lines'][number], textColor: string): void {
  ctx.font = `${line.section === 'headline' ? '800' : '600'} ${line.size}px ${line.section === 'headline' ? '"Barlow Condensed", Arial, sans-serif' : '"Inter", Arial, sans-serif'}`;
  const words = line.text.split(/\s+/u).filter(Boolean);
  const widths = words.map((word) => ctx.measureText(word).width);
  const space = ctx.measureText(' ').width;
  const totalWords = widths.reduce((sum, value) => sum + value, 0);
  const gaps = Math.max(0, words.length - 1);
  const maxWidth = 1080 - line.x * 2;
  const gap = line.justify && gaps > 0 ? space + Math.max(0, (maxWidth - totalWords - space * gaps) / gaps) : space;
  const target = line.highlight.word.toLocaleLowerCase('pl-PL');
  let x = line.x;
  words.forEach((word, index) => {
    const normalized = word.replace(/[^\p{L}\p{N}'’-]/gu, '').toLocaleLowerCase('pl-PL');
    ctx.fillStyle = normalized && normalized === target ? line.highlight.color : textColor;
    ctx.fillText(word, x, line.y);
    x += (widths[index] ?? 0) + gap;
  });
}

/**
 * Compose the final 1080×1080 WBO graphic from an already generated image and a
 * deterministic typography plan. Text is always drawn here — never in the AI
 * image — so changing text, colour or layout never regenerates the picture.
 */
export function renderWboGraphic(input: {
  canvas: HTMLCanvasElement;
  image: CanvasImageSource & { width: number; height: number };
  candidateId: string;
  headline: string;
  description: string;
  config: WboGraphicConfig;
  manual: WboLineHighlight[];
  imageTransform?: { x: number; y: number; zoom: number };
  imageIsIllustration?: boolean;
  format?: WboFormatChoice;
  seed?: number;
  tokenOverrides?: WboTokenOverrides | null;
}): WboGraphicPlan {
  const canvas = input.canvas;
  canvas.width = 1080; canvas.height = 1080;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { format: null, photoHeight: 0, panelHeight: 0, lines: [], seed: input.seed ?? 0, needsEdit: false, error: 'Przeglądarka nie udostępnia Canvas 2D.' };
  const measure = (text: string, font: string) => { ctx.font = font; return ctx.measureText(text).width; };
  const tokens = mergeWboTokens(WBO_TYPOGRAPHY, input.tokenOverrides ?? undefined);
  const plan = planWboTypography({
    headline: input.headline, description: input.description, measure,
    candidateId: input.candidateId, config: { highlightMode: input.config.highlightMode, palette: input.config.palette },
    manual: input.manual, format: input.format ?? 'AUTO', seed: input.seed,
    tokens,
  });
  if (plan.error) return plan;

  const image = input.image;
  const transform = input.imageTransform ?? { x: 0, y: 0, zoom: 1 };
  const photo = wboPhotoBox(plan.photoHeight, tokens);
  const scale = Math.max(photo.width / image.width, photo.height / image.height);
  const cropWidth = photo.width / (scale * transform.zoom); const cropHeight = photo.height / (scale * transform.zoom);
  const sourceX = (image.width - cropWidth) * ((transform.x + 1) / 2); const sourceY = (image.height - cropHeight) * ((transform.y + 1) / 2);
  ctx.fillStyle = PANEL_BACKGROUND; ctx.fillRect(0, 0, 1080, 1080);
  ctx.drawImage(image, sourceX, sourceY, cropWidth, cropHeight, photo.x, photo.y, photo.width, photo.height);
  ctx.fillStyle = PANEL_BACKGROUND; ctx.fillRect(0, plan.photoHeight, 1080, plan.panelHeight);
  for (const line of plan.lines) drawHighlightedLine(ctx, line, input.config.textColor);
  ctx.strokeStyle = input.config.borderColor; ctx.lineWidth = input.config.borderWidth;
  ctx.strokeRect(input.config.borderWidth / 2, input.config.borderWidth / 2, 1080 - input.config.borderWidth, 1080 - input.config.borderWidth);
  return plan;
}

export function planHighlightsToSave(plan: WboGraphicPlan): WboLineHighlight[] {
  return plan.lines.map((line) => ({ section: line.section, line: line.index, lineText: line.text, word: line.highlight.word, color: line.highlight.color }));
}
